import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import styled from 'styled-components';

import LandingPageHome from '../components/LandingPageHome';
import { NextPageWithLayout } from '../components/layouts';
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import useRoutePrefetch from '../hooks/useRoutePrefetch';
import { DefaultLocale } from '../providers/tolgee';
import APILogger from '../services/APILogger';
import CoreAPIService from '../services/CoreAPIService';
import { isProduction } from '../utils/client';
import { generateUniqueId } from '../utils/general';
import {
  detectAndBestEffortSetLanguage,
  getLocaleRedirectIfNeeded,
} from '../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../utils/react-query-utils';

const Home: NextPageWithLayout = () => {
  useRoutePrefetch('/[tenantId]/home');
  useRoutePrefetch('/[tenantId]/lessons');
  useRoutePrefetch('/[tenantId]/workspace/[workspaceId]');

  return (
    <Container>
      <LandingPageLayout
        title="Curipod - Writing Practice Students Love"
        subTitle="Teacher led lessons aligned to your district's curriculum. Students read, write and discuss. Not alone on the screen. Together."
        locale={DefaultLocale}
        hideBlobs={true}
        disableHeadline={true}
      >
        <LandingPageHome />
      </LandingPageLayout>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
`;

/**
 * Allowlisted query parameters that should survive the `/ → /[tenantId]/home`
 * server redirect for authenticated visitors. Keep this list narrow — only
 * params that the destination page knows how to consume.
 */
const HOME_FORWARDED_QUERY_PARAMS = [
  'selectedCardId',
  // Phonics-practice landing pages add these so the Phonics Wizard form
  // opens with the curriculum + objective pre-selected.
  'phonicsCurriculum',
  'phonicsObjective',
] as const;

function forwardedHomeQuery(query: GetServerSidePropsContext['query']): string {
  const params = new URLSearchParams();
  for (const key of HOME_FORWARDED_QUERY_PARAMS) {
    const value = query[key];
    if (typeof value === 'string' && value.length > 0) {
      params.set(key, value);
    }
  }
  const search = params.toString();
  return search ? `?${search}` : '';
}

const TIMEOUT = 20000;
export const getServerSideProps: GetServerSideProps = async context => {
  const logger = new APILogger();
  logger.setRequestId(
    (context.req.headers['x-cloud-trace-context'] as string) ||
      generateUniqueId().toString(),
  );
  try {
    const sessionPromise = getSession({ req: context.req });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), TIMEOUT),
    );

    const session = (await Promise.race([
      sessionPromise,
      timeoutPromise,
    ])) as Session | null;

    if (session) {
      // A failed new-auth provisioning leaves an errored session with no usable
      // user (uid is undefined). Don't attempt a tenant lookup or fall through to
      // the landing page — redirect straight to the styled error page server-side
      // so there's no visible home-page flash before the client recovery kicks in.
      if (session.error === 'ProvisioningError') {
        const params = new URLSearchParams();
        params.set('error_type', session.authErrorType ?? 'oauthCallbackError');
        if (session.correlationId) {
          params.set('correlation_id', session.correlationId);
        }
        // Preserve the non-prod QA override so staging testers (no US_AUTH flag,
        // and a possibly-expired curipod_auth_flow cookie) still get AuthErrorView.
        if (!isProduction()) params.set('newauth', '1');
        return {
          redirect: {
            permanent: false,
            destination: `/auth/error?${params.toString()}`,
          },
        };
      }

      const { activeTenantId } = await CoreAPIService.fetchActiveTenantId(
        session.user.uid,
        logger,
      );

      // Forward allowlisted query params so post-signup deep links (e.g.
      // /?selectedCardId=activity-phonics-wizard from the phonics SEO pages)
      // survive the tenant-aware redirect into the user's home.
      const forwarded = forwardedHomeQuery(context.query);

      return {
        redirect: {
          permanent: false,
          destination: `/${activeTenantId}/home${forwarded}`,
        },
      };
    }

    // Non-authenticated: redirect non-default locale users to their localized landing.
    // Must come after the session check to avoid a redirect loop with /lang/[locale]/
    // which redirects authenticated users back to /.
    const localeRedirect = getLocaleRedirectIfNeeded(context, '/', { logger });
    if (localeRedirect) return localeRedirect;

    const queryClient = new QueryClient();

    return {
      props: {
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context, {
          routeClass: 'nocache',
          logger,
        })),
      },
    };
  } catch (error) {
    logger.error('INDEX_PAGE_GSSP_ERROR', {
      error: error instanceof Error ? error.message : JSON.stringify(error),
    });

    // On session check failure, still redirect non-default locale users.
    const localeRedirect = getLocaleRedirectIfNeeded(context, '/', { logger });
    if (localeRedirect) return localeRedirect;

    return {
      props: {
        ...(await detectAndBestEffortSetLanguage(context, {
          routeClass: 'nocache',
          logger,
        })),
      },
    };
  }
};

export default Home;
